import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, RefreshCw, ExternalLink, X, AlertTriangle, RotateCcw,
  FileText, ChevronDown, ChevronRight, CheckCircle, XCircle, Layers,
  BarChart3,
} from 'lucide-react';
import { authHeaders } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

/* ── Types ─────────────────────────────────────────── */

interface RunPhase {
  phase: string;
  status: string;
  duration_ms?: number;
  model?: string;
  tokens_in?: number;
  tokens_out?: number;
  cost_usd?: number;
  detail?: Record<string, unknown>;
  attempts?: Array<{ status: string; duration_ms: number; detail?: Record<string, unknown> }>;
}

interface RunSummary {
  run_id: string;
  project: string;
  message: string;
  status: string;
  total_duration_ms: number;
  total_cost_usd: number;
  created_at: string;
  completed_at?: string;
  phase_count: number;
  issue_number?: number;
  issue_url?: string;
  pr_number?: number;
  pr_url?: string;
  phases?: RunPhase[];
  // Decomposition fields
  is_parent?: boolean;
  parent_run_id?: string;
  total_subtasks?: number;
  completed_subtasks?: number;
  failed_subtasks?: number;
  group_id?: string;
  subtask_index?: number;
  error_message?: string;
  // Child runs (populated client-side)
  children?: RunSummary[];
}

/** Map raw PipelineRun rows (from /api/runs) to RunSummary shape */
function toRunSummary(raw: Record<string, unknown>): RunSummary {
  return {
    run_id: (raw.run_id ?? raw.id ?? '') as string,
    project: (raw.project ?? raw.project_id ?? '') as string,
    message: (raw.message ?? raw.input_text ?? raw.trigger ?? '') as string,
    status: (raw.status ?? 'unknown') as string,
    total_duration_ms: (raw.total_duration_ms ?? raw.duration_ms ?? 0) as number,
    total_cost_usd: (raw.total_cost_usd ?? raw.cost ?? 0) as number,
    created_at: (raw.created_at ?? raw.started_at ?? '') as string,
    completed_at: raw.completed_at as string | undefined,
    phase_count: (raw.phase_count ?? 0) as number,
    issue_number: raw.issue_number as number | undefined,
    issue_url: raw.issue_url as string | undefined,
    pr_number: raw.pr_number as number | undefined,
    pr_url: raw.pr_url as string | undefined,
    phases: raw.phases as RunPhase[] | undefined,
    is_parent: raw.is_parent as boolean | undefined,
    parent_run_id: raw.parent_run_id as string | undefined,
    total_subtasks: raw.total_subtasks as number | undefined,
    completed_subtasks: raw.completed_subtasks as number | undefined,
    failed_subtasks: raw.failed_subtasks as number | undefined,
    group_id: raw.group_id as string | undefined,
    subtask_index: raw.subtask_index as number | undefined,
    error_message: (raw.error_message ?? raw.error) as string | undefined,
  };
}

/** Group child runs under their parent, return only top-level runs */
function buildRunTree(runs: RunSummary[]): RunSummary[] {
  const parentMap = new Map<string, RunSummary[]>();
  const topLevel: RunSummary[] = [];

  for (const run of runs) {
    if (run.parent_run_id) {
      if (!parentMap.has(run.parent_run_id)) parentMap.set(run.parent_run_id, []);
      parentMap.get(run.parent_run_id)!.push(run);
    } else {
      topLevel.push(run);
    }
  }

  // Attach children to parents, sorted by group_id then subtask_index
  for (const run of topLevel) {
    const children = parentMap.get(run.run_id);
    if (children) {
      run.children = children.sort((a: RunSummary, b: RunSummary) => {
        const gCmp = (a.group_id ?? '').localeCompare(b.group_id ?? '');
        if (gCmp !== 0) return gCmp;
        return (a.subtask_index ?? 0) - (b.subtask_index ?? 0);
      });
    }
  }

  return topLevel;
}

interface LogsProps {
  onSelectRun: (runId: string) => void;
}

/* ── Helpers ───────────────────────────────────────── */

function formatDuration(ms: number): string {
  if (!ms || ms <= 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const PHASES_ALL = ['classify', 'issue', 'spec', 'implement', 'ci', 'review', 'merge', 'deploy'];

const PHASE_LABELS: Record<string, string> = {
  classify: 'Classified', issue: 'Issue Created', spec: 'Spec Generated',
  implement: 'Implemented', ci: 'CI', review: 'Review', merge: 'Merged', deploy: 'Deployed',
};

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  complete: { label: 'Complete', cls: 'badge-complete' },
  completed: { label: 'Complete', cls: 'badge-complete' },
  success: { label: 'Complete', cls: 'badge-complete' },
  merged: { label: 'Merged', cls: 'badge-complete' },
  deployed: { label: 'Deployed', cls: 'badge-complete' },
  deploying: { label: 'Deploying', cls: 'badge-running' },
  deploy_failed: { label: 'Deploy Failed', cls: 'badge-failed' },
  running: { label: 'Running', cls: 'badge-running' },
  in_progress: { label: 'Running', cls: 'badge-running' },
  implementing: { label: 'Building', cls: 'badge-running' },
  reviewing: { label: 'Reviewing', cls: 'badge-running' },
  speccing: { label: 'Planning', cls: 'badge-running' },
  discovering: { label: 'Analyzing', cls: 'badge-running' },
  ci_running: { label: 'Testing', cls: 'badge-running' },
  needs_human: { label: 'Waiting', cls: 'badge-gate' },
  failed: { label: 'Failed', cls: 'badge-failed' },
  cancelled: { label: 'Cancelled', cls: 'badge-pending' },
  gate: { label: 'Awaiting Approval', cls: 'badge-gate' },
  awaiting_consent: { label: 'Awaiting Approval', cls: 'badge-gate' },
  pending: { label: 'Queued', cls: 'badge-pending' },
  partial_failure: { label: 'Partial', cls: 'badge-gate' },
  decomposing: { label: 'Decomposing', cls: 'badge-running' },
};

function projColor(id: string): string {
  const PALETTE = ['#00E5FF','#22c55e','#3b82f6','#a855f7','#06b6d4','#ec4899','#eab308','#14b8a6'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

/* ── Sub-components ────────────────────────────────── */

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.pending;
  return (
    <span className={`badge ${s.cls}`}>
      {status === 'running' || status === 'in_progress'
        ? <span className="spinner" style={{ width: 8, height: 8, borderWidth: 1.5 }} />
        : <span className="badge-dot" />}
      {s.label}
    </span>
  );
};

const PhaseMini: React.FC<{ phases: string[] }> = ({ phases }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    {phases.map((p, i) => {
      const cls = p === 'done' || p === 'complete' || p === 'completed' ? 'done'
        : p === 'running' || p === 'in_progress' ? 'running'
        : p === 'failed' ? 'failed' : '';
      return (
        <div key={i} title={PHASES_ALL[i]} style={{
          width: 18, height: 4, borderRadius: 2,
          background: cls === 'done' ? 'var(--green)' : cls === 'running' ? 'var(--blue)' : cls === 'failed' ? 'var(--red)' : 'var(--surface3)',
          animation: cls === 'running' ? 'pulse 1s infinite' : undefined,
        }} />
      );
    })}
  </div>
);

/* ── Detail Panel ──────────────────────────────────── */

const DetailPanel: React.FC<{
  run: RunSummary;
  onClose: () => void;
  onNavigate: (runId: string) => void;
}> = ({ run, onClose, onNavigate }) => {
  const [runDetail, setRunDetail] = useState<RunSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    authHeaders()
      .then(auth => fetch(`${API_BASE}/api/runs/${run.run_id}`, { headers: auth }))
      .then(r => r.ok ? r.json() : null)
      .then(data => { setRunDetail(data); setLoading(false); })
      .catch(() => { setRunDetail(null); setLoading(false); });
  }, [run.run_id]);

  const phases = runDetail?.phases || [];

  // Build phase status array from API data
  const phaseStatuses = PHASES_ALL.map(phaseName => {
    const found = phases.find(p => p.phase === phaseName);
    if (!found) return 'pending';
    if (found.status === 'complete' || found.status === 'completed' || found.status === 'success') return 'done';
    if (found.status === 'in_progress' || found.status === 'running') return 'running';
    if (found.status === 'failed') return 'failed';
    return 'pending';
  });

  // Cost breakdown by model
  const costByModel: Record<string, { tokens: number; cost: number }> = {};
  for (const phase of phases) {
    if (phase.model && phase.cost_usd) {
      if (!costByModel[phase.model]) costByModel[phase.model] = { tokens: 0, cost: 0 };
      costByModel[phase.model].tokens += (phase.tokens_in || 0) + (phase.tokens_out || 0);
      costByModel[phase.model].cost += phase.cost_usd;
    }
  }

  const handleRerun = async () => {
    try {
      const auth = await authHeaders();
      await fetch(`${API_BASE}/api/runs/${run.run_id}/rerun`, { method: 'POST', headers: auth });
    } catch { /* ignore */ }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: '#00000060', zIndex: 50,
        display: 'flex', justifyContent: 'flex-end',
        animation: 'fadeIn 0.15s ease',
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideIn { from { transform: translateX(40px); opacity:0 } to { transform:translateX(0); opacity:1 } }
      `}</style>
      <div
        style={{
          width: 480, background: 'var(--surface)', borderLeft: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', height: '100%',
          animation: 'slideIn 0.2s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)', marginBottom: 3, maxWidth: 380, lineHeight: 1.4 }}>
              {run.message}
            </div>
            <div className="mono muted" style={{ fontSize: 'var(--text-xs)', display: 'flex', gap: 10 }}>
              <span>{run.run_id.slice(0, 12)}</span>
              <span>·</span>
              <span style={{ color: projColor(run.project) }}>{run.project}</span>
              <span>·</span>
              <span>{timeAgo(run.created_at)}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              cursor: 'pointer', padding: 2, borderRadius: 'var(--radius-sm)',
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '16px 18px',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}>
          {loading ? (
            <div className="empty-state">
              <div className="spinner" />
              <span className="muted">Loading run details...</span>
            </div>
          ) : (
            <>
              {/* Pipeline Timeline */}
              <div>
                <div className="section-label">Pipeline Timeline</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {PHASES_ALL.map((phase, i) => {
                    const pStatus = phaseStatuses[i];
                    const phaseData = phases.find(p => p.phase === phase);
                    const iconBg = pStatus === 'done' ? 'var(--green-dim)' : pStatus === 'running' ? 'var(--blue-dim)' : pStatus === 'failed' ? 'var(--red-dim)' : 'var(--surface3)';
                    const iconColor = pStatus === 'done' ? 'var(--green)' : pStatus === 'running' ? 'var(--blue)' : pStatus === 'failed' ? 'var(--red)' : 'var(--text-muted)';

                    return (
                      <div key={phase} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: '8px 6px', borderRadius: 'var(--radius-sm)',
                        borderBottom: i < PHASES_ALL.length - 1 ? '1px solid var(--border)' : 'none',
                        cursor: 'pointer', transition: 'background var(--transition-fast)',
                      }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: '50%', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', fontSize: 9,
                          flexShrink: 0, marginTop: 1, background: iconBg, color: iconColor,
                        }}>
                          {pStatus === 'running' ? <div className="spinner" style={{ width: 10, height: 10, borderWidth: 1.5 }} />
                            : pStatus === 'done' ? '✓'
                            : pStatus === 'failed' ? '✕' : '·'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', fontWeight: 'var(--weight-medium)' }}>
                            {PHASE_LABELS[phase] || phase}
                          </div>
                          {pStatus !== 'pending' && phaseData && (
                            <div className="mono muted" style={{ fontSize: 'var(--text-xs)', marginTop: 1 }}>
                              {phaseData.status === 'failed' ? 'Failed — max retries exceeded'
                                : pStatus === 'running' ? 'In progress...'
                                : phaseData.detail ? summarizeDetail(phase, phaseData.detail)
                                : '—'}
                            </div>
                          )}
                        </div>
                        <div className="mono muted" style={{ fontSize: 'var(--text-xs)', flexShrink: 0 }}>
                          {phaseData?.duration_ms ? formatDuration(phaseData.duration_ms) : '—'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Cost Breakdown */}
              {Object.keys(costByModel).length > 0 && (
                <div>
                  <div className="section-label">Cost Breakdown</div>
                  <div style={{
                    background: 'var(--surface2)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)', overflow: 'hidden',
                  }}>
                    {Object.entries(costByModel).map(([model, data]) => (
                      <div key={model} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', borderBottom: '1px solid var(--border)',
                        fontSize: 'var(--text-xs)',
                      }}>
                        <span className="mono dim">{model}</span>
                        <span className="mono" style={{ color: 'var(--text)', fontWeight: 'var(--weight-medium)' }}>
                          {data.tokens.toLocaleString()} tokens · ${data.cost.toFixed(3)}
                        </span>
                      </div>
                    ))}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 12px', background: 'var(--surface3)',
                      fontSize: 'var(--text-xs)',
                    }}>
                      <span className="mono dim">Total</span>
                      <span className="mono" style={{ color: 'var(--orange)', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' }}>
                        ${run.total_cost_usd.toFixed(3)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* If no cost data from API, show a simple total */}
              {Object.keys(costByModel).length === 0 && run.total_cost_usd > 0 && (
                <div>
                  <div className="section-label">Cost</div>
                  <div style={{
                    background: 'var(--surface2)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)', padding: '8px 12px',
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: 'var(--text-xs)',
                  }}>
                    <span className="mono dim">Total</span>
                    <span className="mono" style={{ color: 'var(--orange)', fontWeight: 'var(--weight-medium)' }}>
                      ${run.total_cost_usd.toFixed(4)}
                    </span>
                  </div>
                </div>
              )}

              {/* Failed alert */}
              {(run.status === 'failed') && (
                <div style={{
                  background: 'var(--red-dim)', border: '1px solid var(--red)',
                  borderRadius: 'var(--radius-md)', padding: '10px 12px',
                }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <div style={{ color: 'var(--red)', marginTop: 1 }}><AlertTriangle size={13} /></div>
                    <div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--red)', fontWeight: 'var(--weight-semibold)', marginBottom: 3 }}>
                        Pipeline Failed
                      </div>
                      <div className="mono muted" style={{ fontSize: 'var(--text-xs)', lineHeight: 1.5 }}>
                        {run.issue_number ? `Issue #${run.issue_number} remains open. ` : ''}Review logs and resubmit.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Gate alert */}
              {(run.status === 'gate' || run.status === 'awaiting_consent') && (
                <div style={{
                  background: 'var(--yellow-dim)', border: '1px solid var(--yellow)',
                  borderRadius: 'var(--radius-md)', padding: '10px 12px',
                }}>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--yellow)', fontWeight: 'var(--weight-semibold)', marginBottom: 6 }}>
                    High Risk — Awaiting Approval
                  </div>
                  <div className="mono muted" style={{ fontSize: 'var(--text-xs)', marginBottom: 10 }}>
                    Claude classified this as high risk. Review the spec before approving.
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm">Approve</button>
                    <button className="btn btn-danger btn-sm">Reject</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--border)',
          display: 'flex', gap: 8, flexShrink: 0,
        }}>
          {run.pr_url && (
            <a href={run.pr_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ textDecoration: 'none' }}>
              <ExternalLink size={11} /> View PR #{run.pr_number}
            </a>
          )}
          <button className="btn btn-sm" onClick={() => onNavigate(run.run_id)}>
            <FileText size={11} /> Full Logs
          </button>
          {run.status === 'failed' && (
            <button className="btn btn-primary btn-sm" onClick={handleRerun}>
              <RotateCcw size={11} /> Re-run
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function summarizeDetail(phase: string, detail: Record<string, unknown>): string {
  if (phase === 'classify') {
    const c = detail.classification as Record<string, unknown> | undefined;
    if (c) return `${c.type || '—'} · ${c.risk || '—'} risk · ${c.route || '—'}`;
  }
  if (phase === 'issue' && detail.issue_number) return `Issue #${detail.issue_number}`;
  if (phase === 'spec' && detail.spec) return 'Spec generated';
  if (phase === 'implement' && detail.commit_sha) return `Commit ${String(detail.commit_sha).slice(0, 7)}`;
  if (phase === 'merge' && detail.pr_number) return `PR #${detail.pr_number} merged`;
  if (phase === 'deploy' && detail.deploy_id) return `Deploy ${String(detail.deploy_id).slice(0, 8)}`;
  return '—';
}

/* ── Main Component ────────────────────────────────── */

export const Logs: React.FC<LogsProps> = ({ onSelectRun }) => {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [selected, setSelected] = useState<RunSummary | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState<{
    decomposed_runs: number;
    avg_subtasks_per_run: number;
    avg_parallel_groups: number;
    subtask_failure_rate: number;
    top_conflict_files: Array<{ file: string; count: number }>;
    total_subtask_runs: number;
    failed_subtask_runs: number;
  } | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const auth = await authHeaders();
      const res = await fetch(`${API_BASE}/api/logs/decomposition/analytics`, { headers: auth });
      if (res.ok) setAnalytics(await res.json());
    } catch { /* ignore */ }
  }, []);

  const fetchRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = await authHeaders();
      const res = await fetch(`${API_BASE}/api/runs`, { headers: auth });
      if (!res.ok) throw new Error(`Failed to fetch runs: ${res.status}`);
      const data = await res.json();
      const rawRuns = Array.isArray(data) ? data : data.runs || [];
      setRuns(rawRuns.map(toRunSummary));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRuns(); fetchAnalytics(); }, [fetchAnalytics]);

  const projects = useMemo(() => {
    const set = new Set(runs.map(r => r.project));
    return Array.from(set).sort();
  }, [runs]);

  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  const toggleParent = (runId: string) => {
    setExpandedParents((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const treeRuns = useMemo(() => buildRunTree(runs), [runs]);

  const filtered = useMemo(() => {
    return treeRuns.filter((r: RunSummary) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (projectFilter !== 'all' && r.project !== projectFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return r.message.toLowerCase().includes(q) || r.run_id.toLowerCase().includes(q) || r.project.toLowerCase().includes(q);
      }
      return true;
    });
  }, [treeRuns, search, statusFilter, projectFilter]);

  const stats = useMemo(() => {
    // Exclude child runs from top-level stats (parent shows aggregate)
    const topLevel = treeRuns;
    return {
      total: topLevel.length,
      complete: topLevel.filter((r: RunSummary) => r.status === 'complete' || r.status === 'completed' || r.status === 'success' || r.status === 'merged' || r.status === 'deployed').length,
      failed: topLevel.filter((r: RunSummary) => r.status === 'failed').length,
      cost: runs.reduce((s: number, r: RunSummary) => s + (r.total_cost_usd || 0), 0),
      decomposed: topLevel.filter((r: RunSummary) => r.is_parent).length,
    };
  }, [runs, treeRuns]);

  // Build phase pips from run data
  const getRunPhases = (run: RunSummary): string[] => {
    if (run.phases && run.phases.length > 0) {
      return PHASES_ALL.map(phaseName => {
        const found = run.phases!.find(p => p.phase === phaseName);
        if (!found) return 'pending';
        if (found.status === 'complete' || found.status === 'completed' || found.status === 'success') return 'done';
        if (found.status === 'in_progress' || found.status === 'running') return 'running';
        if (found.status === 'failed') return 'failed';
        return 'pending';
      });
    }
    // Fallback: estimate from status + phase_count
    const count = run.phase_count || 0;
    return PHASES_ALL.map((_, i) => {
      if (run.status === 'complete' || run.status === 'completed' || run.status === 'success') return 'done';
      if (run.status === 'failed') return i < count ? (i === count - 1 ? 'failed' : 'done') : 'pending';
      if (run.status === 'in_progress' || run.status === 'running') return i < count ? 'done' : i === count ? 'running' : 'pending';
      return 'pending';
    });
  };

  const handleRerunFailed = async () => {
    const failedRuns = runs.filter(r => r.status === 'failed');
    for (const r of failedRuns) {
      try {
        const rerunAuth = await authHeaders();
        await fetch(`${API_BASE}/api/runs/${r.run_id}/rerun`, { method: 'POST', headers: rerunAuth });
      } catch { /* ignore */ }
    }
    fetchRuns();
  };

  return (
    <>
      {/* Page Header */}
      <div className="page-title-row">
        <div>
          <h2 className="page-title">Pipeline Runs</h2>
          <p className="page-subtitle">All pipeline executions across managed projects</p>
        </div>
        <button className="btn btn-primary" onClick={handleRerunFailed}>
          <RotateCcw size={11} /> Re-run Failed
        </button>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div className="stat-card" style={{ flex: 1 }}>
          <div className="stat-label">Total Runs</div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-sub">last 7 days</div>
        </div>
        <div className="stat-card" style={{ flex: 1 }}>
          <div className="stat-label">Succeeded</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>{stats.complete}</div>
          <div className="stat-sub">{stats.total > 0 ? Math.round(stats.complete / stats.total * 100) : 0}% success rate</div>
        </div>
        <div className="stat-card" style={{ flex: 1 }}>
          <div className="stat-label">Failed</div>
          <div className="stat-value" style={{ color: 'var(--red)' }}>{stats.failed}</div>
          <div className="stat-sub">max 3 retries each</div>
        </div>
        <div className="stat-card" style={{ flex: 1 }}>
          <div className="stat-label">Total Cost</div>
          <div className="stat-value" style={{ color: 'var(--orange)' }}>${stats.cost.toFixed(3)}</div>
          <div className="stat-sub">all engines</div>
        </div>
      </div>

      {/* AC-28: Decomposition Analytics */}
      {stats.decomposed > 0 && (
        <div style={{ marginBottom: 12 }}>
          <button
            className="btn btn-sm"
            onClick={() => { setShowAnalytics(!showAnalytics); if (!analytics) fetchAnalytics(); }}
            style={{ marginBottom: showAnalytics ? 8 : 0 }}
          >
            <BarChart3 size={11} /> {showAnalytics ? 'Hide' : 'Show'} Decomposition Analytics
          </button>

          {showAnalytics && analytics && (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
            }}>
              <div className="stat-card">
                <div className="stat-label">Decomposed Runs</div>
                <div className="stat-value">{analytics.decomposed_runs}</div>
                <div className="stat-sub">{analytics.total_subtask_runs} total subtasks</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Avg Subtasks/Run</div>
                <div className="stat-value">{analytics.avg_subtasks_per_run}</div>
                <div className="stat-sub">{analytics.avg_parallel_groups} avg groups</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Subtask Failure Rate</div>
                <div className="stat-value" style={{ color: analytics.subtask_failure_rate > 10 ? 'var(--red)' : 'var(--green)' }}>
                  {analytics.subtask_failure_rate}%
                </div>
                <div className="stat-sub">{analytics.failed_subtask_runs} failed</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Top Conflict Files</div>
                <div style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)', color: 'var(--text-dim)', marginTop: 4 }}>
                  {analytics.top_conflict_files.length === 0
                    ? <span className="muted">none</span>
                    : analytics.top_conflict_files.slice(0, 3).map((f: { file: string; count: number }) => (
                      <div key={f.file} className="truncate" style={{ maxWidth: 200 }} title={f.file}>
                        {f.file.split('/').pop()} ({f.count})
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, maxWidth: 320, position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={12} style={{ position: 'absolute', left: 10, color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            className="input input-mono"
            style={{ paddingLeft: 30, fontSize: 'var(--text-sm)' }}
            placeholder="Search runs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="input input-mono" style={{ width: 'auto', fontSize: 'var(--text-xs)' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="complete">Complete</option>
          <option value="running">Running</option>
          <option value="in_progress">In Progress</option>
          <option value="failed">Failed</option>
          <option value="awaiting_consent">Awaiting Approval</option>
        </select>
        <select className="input input-mono" style={{ width: 'auto', fontSize: 'var(--text-xs)' }} value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
          <option value="all">All projects</option>
          {projects.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-sm" onClick={fetchRuns}>
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      {loading && runs.length === 0 ? (
        <div className="empty-state">
          <div className="spinner" />
          <span className="muted">Loading runs...</span>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-icon"><AlertTriangle size={18} /></div>
          <div className="empty-state-title">{error}</div>
          <button className="btn btn-sm" onClick={fetchRuns}>
            <RefreshCw size={11} /> Retry
          </button>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 12 }}>
            Encountering a persistent issue?{' '}
            <button
              onClick={() => (window as any).LumenIQSupport?.open()}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', textDecoration: 'underline',
                fontSize: 'var(--text-xs)', fontFamily: 'inherit', padding: 0,
              }}
            >
              Contact support
            </button>
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FileText size={18} /></div>
          <div className="empty-state-title">
            {runs.length === 0 ? 'No runs yet' : 'No runs found'}
          </div>
          <div className="empty-state-desc">
            {runs.length === 0 ? 'Use the Chat interface to start a pipeline.' : 'Try adjusting your filters.'}
          </div>
        </div>
      ) : (
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Request</th>
                <th>Project</th>
                <th>Phases</th>
                <th>Duration</th>
                <th>Cost</th>
                <th>Links</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((run: RunSummary) => {
                const isParent = run.is_parent && run.children && run.children.length > 0;
                const isExpanded = expandedParents.has(run.run_id);

                return (
                  <React.Fragment key={run.run_id}>
                    {/* Main run row */}
                    <tr onClick={() => isParent ? toggleParent(run.run_id) : setSelected(run)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {isParent && (
                            isExpanded
                              ? <ChevronDown size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                              : <ChevronRight size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          )}
                          <StatusBadge status={run.status} />
                        </div>
                      </td>
                      <td>
                        <div className="truncate" style={{ maxWidth: 340, fontSize: 'var(--text-base)', color: 'var(--text)' }}>
                          {run.message}
                        </div>
                        <div className="td-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {run.run_id.slice(0, 12)}
                          {isParent && (
                            <span style={{
                              fontSize: 9, padding: '1px 5px', borderRadius: 3,
                              background: 'var(--surface3)', color: 'var(--text-muted)',
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>
                              <Layers size={8} />
                              {run.total_subtasks} subtasks
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="proj-tag">
                          <span className="project-dot" style={{ background: projColor(run.project), width: 6, height: 6, borderRadius: '50%', display: 'inline-block' }} />
                          {run.project}
                        </span>
                      </td>
                      <td>
                        {isParent ? (
                          <span className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>
                            {run.completed_subtasks ?? 0}/{run.total_subtasks ?? 0}
                            {(run.failed_subtasks ?? 0) > 0 && (
                              <span style={{ color: 'var(--red)', marginLeft: 4 }}>({run.failed_subtasks} failed)</span>
                            )}
                          </span>
                        ) : (
                          <PhaseMini phases={getRunPhases(run)} />
                        )}
                      </td>
                      <td><span className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>{formatDuration(run.total_duration_ms)}</span></td>
                      <td><span className="mono dim" style={{ fontSize: 'var(--text-sm)' }}>${run.total_cost_usd?.toFixed(3) || '0.000'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {run.issue_number && (
                            <a
                              href={run.issue_url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mono"
                              style={{ fontSize: 'var(--text-xs)', color: 'var(--orange)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, opacity: 0.8 }}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.opacity = '1')}
                              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.opacity = '0.8')}
                            >
                              #{run.issue_number} <ExternalLink size={9} />
                            </a>
                          )}
                          {run.pr_number && (
                            <a
                              href={run.pr_url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mono"
                              style={{ fontSize: 'var(--text-xs)', color: 'var(--orange)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, opacity: 0.8 }}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.opacity = '1')}
                              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.opacity = '0.8')}
                            >
                              PR #{run.pr_number} <ExternalLink size={9} />
                            </a>
                          )}
                        </div>
                      </td>
                      <td><span className="mono muted" style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>{timeAgo(run.created_at)}</span></td>
                    </tr>

                    {/* Child subtask rows (AC-27) */}
                    {isParent && isExpanded && run.children!.map((child: RunSummary) => (
                      <tr
                        key={child.run_id}
                        onClick={() => setSelected(child)}
                        style={{ cursor: 'pointer', background: 'var(--surface2)' }}
                      >
                        <td style={{ paddingLeft: 32 }}>
                          {child.status === 'merged' || child.status === 'deployed' || child.status === 'completed'
                            ? <CheckCircle size={12} style={{ color: 'var(--green)' }} />
                            : child.status === 'failed'
                              ? <XCircle size={12} style={{ color: 'var(--red)' }} />
                              : <StatusBadge status={child.status} />}
                        </td>
                        <td>
                          <div className="truncate" style={{ maxWidth: 320, fontSize: 'var(--text-sm)', color: 'var(--text-dim)', paddingLeft: 12 }}>
                            {child.message}
                          </div>
                          {child.status === 'failed' && (
                            <div style={{
                              fontSize: 10, color: 'var(--red)', fontFamily: 'var(--font-mono)',
                              paddingLeft: 12, marginTop: 2, maxWidth: 320,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {child.error_message || 'failed — see logs'}
                            </div>
                          )}
                        </td>
                        <td />
                        <td><PhaseMini phases={getRunPhases(child)} /></td>
                        <td><span className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>{formatDuration(child.total_duration_ms)}</span></td>
                        <td><span className="mono dim" style={{ fontSize: 'var(--text-sm)' }}>${child.total_cost_usd?.toFixed(3) || '0.000'}</span></td>
                        <td>
                          {child.pr_number && (
                            <a
                              href={child.pr_url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mono"
                              style={{ fontSize: 'var(--text-xs)', color: 'var(--orange)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, opacity: 0.8 }}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.opacity = '1')}
                              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.opacity = '0.8')}
                            >
                              PR #{child.pr_number} <ExternalLink size={9} />
                            </a>
                          )}
                        </td>
                        <td><span className="mono muted" style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>{timeAgo(child.created_at)}</span></td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Panel */}
      {selected && (
        <DetailPanel
          run={selected}
          onClose={() => setSelected(null)}
          onNavigate={(id) => { setSelected(null); onSelectRun(id); }}
        />
      )}
    </>
  );
};
